/**
 * Considerations:
 * - eventemitter2 is too heavy for this
 * - encourage to use wrappers such as ExperienceProtocol
 */
import { EventEmitter2 } from 'eventemitter2'
import { type EmbedElementContext } from './element-embedder'

export type EmbedEventHandlers<E = any> = (payload: E, embedInfo: EmbedElementContext, source: string) => void

export interface EmbedEventEmitterOptions {
  global?: boolean
}
export class LocalEmbedEventEmitter {
  #eventEmitter: EventEmitter2
  id: string
  constructor (id: string, eventEmitter: EventEmitter2) {
    this.id = id
    this.#eventEmitter = eventEmitter
  }

  private getEventName (options: EmbedEventEmitterOptions | undefined, name: string) {
    return options?.global === true ? name : `__room__.${this.id}.${name}`
  }

  on<K extends Extract<keyof LocalEventMap, string>>(name: K, callback: EmbedEventHandlers<LocalEventMap[K]>, options?: EmbedEventEmitterOptions) {
    const eventName = this.getEventName(options, name)
    this.#eventEmitter.on(eventName, callback)
  }

  off<K extends Extract<keyof LocalEventMap, string>>(name: K, callback: EmbedEventHandlers<LocalEventMap[K]>, options?: EmbedEventEmitterOptions) {
    const eventName = this.getEventName(options, name)
    this.#eventEmitter.off(eventName, callback)
  }

  emit<K extends Extract<keyof LocalEventMap, string>>(name: K, payload: any, options?: EmbedEventEmitterOptions) {
    const eventName = this.getEventName(options, name)
    this.#eventEmitter.emit(eventName, payload)
  }

  destroy () {
    this.#eventEmitter.removeAllListeners()
  }
}

interface LocalEventMap extends GlobalEventMap {}

export class GlobalEmbedEventEmitter {
  #eventEmitter = new EventEmitter2({ wildcard: true })

  on<K extends Extract<keyof GlobalEventMap, string>>(name: K, callback: EmbedEventHandlers<GlobalEventMap[K]>) {
    this.#eventEmitter.on(name, callback)
  }

  off<K extends Extract<keyof GlobalEventMap, string>>(name: K, callback: EmbedEventHandlers<GlobalEventMap[K]>) {
    this.#eventEmitter.off(name, callback)
  }

  emit<K extends Extract<keyof GlobalEventMap, string>>(name: K, payload: GlobalEventMap[K]) {
    this.#eventEmitter.emit(name, payload)
  }

  getRoom (id: string) {
    return new LocalEmbedEventEmitter(id, this.#eventEmitter)
  }

  destroy () {
    this.#eventEmitter.removeAllListeners()
  }
}

interface GlobalEventMap {
  'window.resize': { innerWidth: number, innerHeight: number }
  [k: string]: any
}
